/**
 * components/hire/editCandidatePiiModal.tsx
 * Edit a candidate — mirrors the Add-candidate fields you can change:
 *   • contact details (name / email / phone) → PATCH /candidates/:id (PII;
 *     needs the 0012 guard relax)
 *   • resume text (optional) → POST /candidates/:id/resumes, which appends a new
 *     version and re-extracts the profile (skills, experience, location, titles)
 *
 * Saving asks for confirmation and shows a success dialog (both as in-modal
 * overlays). Client-side validation blocks the API call when name/email are
 * missing/invalid or when nothing changed. All outcomes go through the toasts.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { Modal, ActionButton, Icon } from '../ui';
import { web } from '../../utils/web';
import { showToast } from '../../components/toastComponent';
import { getApiErrorMessage } from '../../utils/errorHandler';
import { updateCandidate, addResume } from '../../services/hire/candidateV2';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';
import Email_Component from '../emailInput';
import MobileNumberInput, { COUNTRIES, type CountryEntry } from '../mobileNumberInput';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const IN_COUNTRY = COUNTRIES.find((c) => c.code === 'IN') ?? COUNTRIES[0];

/** Split a stored full phone (e.g. "+919876543210") into country + national digits. */
function parsePhone(raw: string | null): { country: CountryEntry; national: string } {
  if (!raw) return { country: IN_COUNTRY, national: '' };
  const digits = raw.replace(/\D/g, '');
  // Longest dial code first so "+1809" beats "+1"; require the remainder to be
  // exactly the country's digit count for a confident match.
  const byLen = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);
  for (const c of byLen) {
    const dc = c.dialCode.replace('+', '');
    if (digits.startsWith(dc) && digits.length - dc.length === c.digitCount) {
      return { country: c, national: digits.slice(dc.length) };
    }
  }
  return { country: IN_COUNTRY, national: digits.slice(-IN_COUNTRY.digitCount) };
}

export default function EditCandidatePiiModal({
  open, candidate, onClose, onUpdated,
}: {
  open: boolean;
  candidate: CandidateDetail | null;
  onClose: () => void;
  onUpdated: (c: CandidateDetail) => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');                       // national digits only
  const [phoneCountry, setPhoneCountry] = useState<CountryEntry>(IN_COUNTRY);
  const [resume, setResume] = useState('');
  const [invalid, setInvalid] = useState<{ name?: boolean; email?: boolean }>({});
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [success, setSuccess] = useState<CandidateDetail | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(candidate?.name ?? '');
    setEmail(candidate?.email ?? '');
    const parsed = parsePhone(candidate?.phone ?? null);
    setPhoneCountry(parsed.country);
    setPhone(parsed.national);
    setResume('');
    setInvalid({});
    setConfirmOpen(false);
    setSuccess(null);
  }, [open, candidate]);

  const close = () => { if (!busy) onClose(); };

  const fullPhone = phone.length > 0 ? `${phoneCountry.dialCode}${phone}` : null;
  const piiChanged =
    name.trim() !== (candidate?.name ?? '') ||
    email.trim() !== (candidate?.email ?? '') ||
    fullPhone !== (candidate?.phone ?? null);
  const hasResume = !!resume.trim();

  const onSaveClick = () => {
    const bad: { name?: boolean; email?: boolean } = {};
    if (!name.trim()) bad.name = true;
    if (!email.trim() || !EMAIL_RE.test(email.trim())) bad.email = true;
    setInvalid(bad);
    if (bad.name) { showToast('error', 'Name is required', 'Enter the candidate name.'); return; }
    if (bad.email) { showToast('error', 'A valid email is required', 'Enter a valid email address.'); return; }
    if (phone.length > 0 && phone.length !== phoneCountry.digitCount) {
      showToast('error', 'Incomplete phone number', `${phoneCountry.name} numbers need ${phoneCountry.digitCount} digits, or leave it blank.`);
      return;
    }
    if (!piiChanged && !hasResume) { showToast('error', 'Nothing to save', 'Change a contact detail or paste a new resume first.'); return; }
    setConfirmOpen(true);
  };

  const onConfirm = async () => {
    if (!candidate) return;
    setBusy(true);
    try {
      let updated = candidate;
      if (piiChanged) updated = await updateCandidate(candidate.id, { name: name.trim(), email: email.trim(), phone: fullPhone });
      if (hasResume) updated = await addResume(candidate.id, resume.trim());
      setConfirmOpen(false);
      setSuccess(updated);
    } catch (e) {
      setConfirmOpen(false);
      showToast('error', 'Could not save changes', getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onDone = () => {
    const u = success;
    setSuccess(null);
    onClose();
    if (u) onUpdated(u);
  };

  // Premium field label — uppercase, matches the ported Email / Phone components.
  const field = (label: string, node: React.ReactNode, required?: boolean) => (
    <View style={{ gap: 8, width: '100%' }}>
      <Text style={{ fontSize: 12, color: colors.ink3, fontWeight: '800', letterSpacing: 1.5, textTransform: 'uppercase', paddingHorizontal: 4 }}>
        {label}{required && <Text style={{ color: colors.danger }}> *</Text>}
      </Text>
      {node}
    </View>
  );

  // Premium input — card surface, 1.5px border, matches the SK-PLATFORM components.
  const input = (value: string, set: (v: string) => void, placeholder: string, errored: boolean, clearKey: 'name' | 'email' | null, keyboard?: 'email-address' | 'phone-pad') => (
    <TextInput
      value={value}
      onChangeText={(v) => { set(v); if (errored && clearKey) setInvalid((p) => ({ ...p, [clearKey]: false })); }}
      placeholder={placeholder}
      placeholderTextColor={colors.ink4}
      autoCapitalize={keyboard === 'email-address' ? 'none' : 'words'}
      keyboardType={keyboard ?? 'default'}
      style={[
        { height: 44, paddingHorizontal: 12, borderWidth: 1.5, borderColor: errored ? colors.danger : colors.lineStrong, borderRadius: 2, backgroundColor: errored ? colors.dangerSoft : colors.card, color: colors.ink, fontSize: 14, fontWeight: '500' },
        web({ outline: 'none', transition: 'border-color 140ms ease' }),
      ]}
    />
  );

  const confirmParts = [piiChanged ? 'update contact details' : null, hasResume ? 'append a new resume version (re-extracts the profile)' : null].filter(Boolean);

  const overlayNode =
    confirmOpen ? (
      <OverlayScrim>
        <OverlayCard accent={colors.accent} shadows={shadows}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accentSoft, alignSelf: 'center' }}>
            <Icon name="user" size={24} color={colors.ink} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>Save changes to {candidate?.name}?</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
            This will {confirmParts.join(' and ')}.{hasResume ? ' Earlier resume versions stay in history.' : ''}
          </Text>
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
            <View style={{ flex: 1 }}><ActionButton label="Keep editing" variant="secondary" onPress={() => setConfirmOpen(false)} /></View>
            <View style={{ flex: 1 }}><ActionButton label="Save changes" variant="primary" loading={busy} onPress={onConfirm} /></View>
          </View>
        </OverlayCard>
      </OverlayScrim>
    ) : success ? (
      <OverlayScrim>
        <OverlayCard accent={colors.success} shadows={shadows}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.successSoft, alignSelf: 'center' }}>
            <Icon name="check-circle" size={28} color={colors.success} />
          </View>
          <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>Candidate updated</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
            {success.name}'s details are saved{success.profile?.resumeVersion ? ` · now on resume v${success.profile.resumeVersion}` : ''}.
          </Text>
          <View style={{ marginTop: 4 }}><ActionButton label="Done" variant="primary" onPress={onDone} /></View>
        </OverlayCard>
      </OverlayScrim>
    ) : undefined;

  return (
    <Modal
      open={open}
      onClose={close}
      title={candidate ? `Edit ${candidate.name}` : 'Edit candidate'}
      size="wide"
      overlay={overlayNode}
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton label="Save changes" variant="primary" onPress={onSaveClick} loading={busy} />
        </View>
      }
    >
      <View style={{ gap: 16 }}>
        {/* Everything currently on record, so the form is edited against the full
            picture rather than three fields in isolation. Read-only: the profile
            fields are corrected from the Summary panel's Edit, and the code /
            consent / version are not user-editable at all. */}
        {!!candidate && (
          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.paper2, overflow: 'hidden' }}>
            <View style={{ paddingHorizontal: 12, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Icon name="user" size={13} color={colors.ink3} />
              <Text style={{ flex: 1, fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.semibold, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                On record
              </Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>{candidate.code}</Text>
            </View>
            <View style={{ padding: 12, gap: 5 }}>
              {([
                ['Name', candidate.name],
                ['Email', candidate.email ?? '—'],
                ['Phone', candidate.phone ?? '—'],
                ['Current role', ((candidate.profile?.titlesHistory ?? []) as { titleSlug?: string }[])[0]?.titleSlug ?? '—'],
                ['Location', candidate.profile?.currentLocation ?? '—'],
                ['Experience', candidate.profile?.totalExperienceYears != null ? `${candidate.profile.totalExperienceYears} y` : '—'],
                ['Notice', candidate.profile?.noticePeriodDays != null ? `${candidate.profile.noticePeriodDays} d` : '—'],
                ['Skills', (candidate.profile?.skills ?? []).length ? `${(candidate.profile?.skills ?? []).length} · ${(candidate.profile?.skills ?? []).slice(0, 5).map((sk) => sk.slug).join(', ')}${(candidate.profile?.skills ?? []).length > 5 ? '…' : ''}` : '—'],
                ['Resume', `v${candidate.profile?.resumeVersion ?? 1}${candidate.sourceFilename ? ` · ${candidate.sourceFilename}` : ''}`],
                ['Consent', candidate.consentStatus],
                ['Status', candidate.status],
              ] as [string, string][]).map(([k, v]) => (
                <View key={k} style={{ flexDirection: 'row', gap: 10 }}>
                  <Text style={{ width: 96, fontSize: fontSize.xs, color: colors.ink4 }}>{k}</Text>
                  <Text style={{ flex: 1, minWidth: 0, fontSize: fontSize.xs, color: colors.ink2 }} numberOfLines={2}>{v}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Contact details */}
        <Section icon="user" title="Contact details">
          {field('Name', input(name, setName, 'e.g. Gokulakrishnan', !!invalid.name, 'name'), true)}
          {/* Email — SK-PLATFORM premium component (live validation) */}
          <Email_Component
            required
            value={email}
            onValueChange={(v) => { setEmail(v); if (invalid.email) setInvalid((p) => ({ ...p, email: false })); }}
            placeholder="e.g. gokul@gmail.com"
          />
          {/* Phone — SK-PLATFORM premium mobile-number component */}
          <MobileNumberInput
            label="PHONE"
            value={phone}
            selectedCountry={phoneCountry}
            onCountryChange={setPhoneCountry}
            onChangeText={setPhone}
            placeholder="e.g. 9876543210 (optional)"
          />
        </Section>

        {/* Resume */}
        <Section icon="edit" title="Resume" hint="optional — appends a new version">
          <TextInput
            value={resume}
            onChangeText={setResume}
            placeholder="Paste an updated resume to append a new version and re-extract skills, experience & location. Leave blank to keep the current resume."
            placeholderTextColor={colors.ink4}
            multiline
            style={[{ minHeight: 130, padding: 12, borderWidth: 1.5, borderColor: colors.lineStrong, borderRadius: 2, backgroundColor: colors.card, color: colors.ink, fontSize: 14, textAlignVertical: 'top' }, web({ outline: 'none' })]}
          />
          <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>
            Contact details save instantly; a new resume re-runs AI extraction (byte-identical re-uploads cost nothing). Consent is managed with Renew / Withdraw.
          </Text>
        </Section>
      </View>
    </Modal>
  );
}

/** A titled section "card" grouping related fields with even spacing. Defined
 *  at module level so it's a stable component type (inputs never remount). */
function Section({ icon, title, hint, children }: { icon: string; title: string; hint?: string; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  return (
    <View style={[{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.lg, backgroundColor: colors.card, padding: 16, gap: 14 }, web({ boxShadow: '0 1px 2px rgba(16,24,40,0.05)' })]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9 }}>
        <View style={{ width: 26, height: 26, borderRadius: 7, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={icon} size={14} color={colors.ink3} />
        </View>
        <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{title}</Text>
        {hint && <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>· {hint}</Text>}
      </View>
      <View style={{ height: 1, backgroundColor: colors.line, marginHorizontal: -16 }} />
      {children}
    </View>
  );
}

function OverlayScrim({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: 'rgba(8,10,14,0.55)' },
        web({ animation: 'sk-rise 160ms ease both', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
      ]}
    >
      {children}
    </View>
  );
}

function OverlayCard({ accent, shadows, children }: { accent: string; shadows: { lg: object }; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii } = theme;
  return (
    <View style={[{ width: '100%', maxWidth: 420, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' }, shadows.lg]}>
      <View style={{ height: 3, backgroundColor: accent }} />
      <View style={{ padding: 20, gap: 12 }}>{children}</View>
    </View>
  );
}
